import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../App";
import { Trash2, Users, Shield, KeyRound } from "lucide-react";
import axios from "axios";

const API = `/api`;

export default function AdminPage() {
  const { user, loading } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [resetId, setResetId] = useState(null);
  const [resetPwd, setResetPwd] = useState("");

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) {
      navigate("/");
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user && user.role === "admin") fetchUsers();
  }, [user]);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/users`);
      setUsers(res.data.users);
    } catch {
    } finally {
      setFetching(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/admin/users/${id}`);
      setUsers(users.filter((u) => u.id !== id));
      setDeleteId(null);
    } catch {}
  };

  const handleResetPassword = async (id) => {
    if (!resetPwd || resetPwd.length < 6) return;
    try {
      await axios.post(`${API}/admin/users/${id}/reset-password`, { new_password: resetPwd });
      setResetId(null);
      setResetPwd("");
      alert(t("auth_password_changed"));
    } catch {}
  };

  if (loading || !user || user.role !== "admin") {
    return (
      <div style={{ minHeight: "100vh", background: "#0D0704", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div className="loading-ring spin-slow" style={{ width: 40, height: 40 }} />
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" style={{ minHeight: "100vh", background: "#0D0704", paddingTop: "64px" }}>
      <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "2.5rem 1.5rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "2rem" }}>
          <Shield size={20} style={{ color: "#C9A84C" }} />
          <h1
            className="font-logo"
            data-testid="admin-title"
            style={{ fontSize: "clamp(1.3rem, 3vw, 1.8rem)", fontWeight: 500, color: "#D4B878", letterSpacing: "0.12em", textTransform: "uppercase" }}
          >
            {t("admin_title")}
          </h1>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "1.5rem" }}>
          <Users size={15} style={{ color: "#B8956A" }} />
          <span style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.82rem", color: "#B8956A", letterSpacing: "0.08em" }}>
            {users.length} {t("admin_users")}
          </span>
        </div>

        {fetching ? (
          <div style={{ textAlign: "center", padding: "3rem" }}>
            <div className="loading-ring spin-slow" style={{ width: 40, height: 40, margin: "0 auto" }} />
          </div>
        ) : users.length === 0 ? (
          <p style={{ color: "#B8956A", fontFamily: "'DM Sans',sans-serif", textAlign: "center", padding: "3rem" }}>
            {t("admin_no_users")}
          </p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table data-testid="admin-users-table" style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid rgba(201,168,76,0.2)" }}>
                  {[t("admin_name"), t("admin_email"), t("admin_role"), t("admin_images"), t("admin_videos"), t("admin_generations"), t("admin_joined"), ""].map((h, i) => (
                    <th
                      key={i}
                      style={{
                        fontFamily: "'DM Sans',sans-serif",
                        fontSize: "0.68rem",
                        fontWeight: 500,
                        color: "rgba(201,168,76,0.6)",
                        letterSpacing: "0.15em",
                        textTransform: "uppercase",
                        padding: "0.75rem 0.5rem",
                        textAlign: "start",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr
                    key={u.id}
                    data-testid={`admin-user-row-${u.id}`}
                    style={{ borderBottom: "1px solid rgba(201,168,76,0.08)" }}
                  >
                    <td style={cellStyle}>{u.name}</td>
                    <td style={cellStyle}>{u.email}</td>
                    <td style={cellStyle}>
                      <span
                        style={{
                          padding: "0.2rem 0.6rem",
                          borderRadius: "999px",
                          fontSize: "0.68rem",
                          letterSpacing: "0.1em",
                          textTransform: "uppercase",
                          border: `1px solid ${u.role === "admin" ? "rgba(201,168,76,0.4)" : "rgba(184,149,106,0.2)"}`,
                          color: u.role === "admin" ? "#D4B878" : "#B8956A",
                        }}
                      >
                        {u.role}
                      </span>
                    </td>
                    <td style={{ ...cellStyle, textAlign: "center" }}>{u.image_count}</td>
                    <td style={{ ...cellStyle, textAlign: "center" }}>{u.video_count}</td>
                    <td style={{ ...cellStyle, textAlign: "center", color: "#D4B878", fontWeight: 500 }}>{u.generation_count}</td>
                    <td style={{ ...cellStyle, fontSize: "0.75rem" }}>
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : "-"}
                    </td>
                    <td style={cellStyle}>
                      {u.role !== "admin" && (
                        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                          {resetId === u.id ? (
                            <div style={{ display: "flex", gap: "0.3rem", alignItems: "center" }}>
                              <input
                                data-testid={`reset-pwd-input-${u.id}`}
                                type="text"
                                placeholder="New password"
                                value={resetPwd}
                                onChange={(e) => setResetPwd(e.target.value)}
                                className="khatira-input"
                                style={{ width: "120px", padding: "0.3rem 0.5rem", fontSize: "0.72rem" }}
                              />
                              <button
                                data-testid={`confirm-reset-${u.id}`}
                                onClick={() => handleResetPassword(u.id)}
                                style={{ padding: "0.3rem 0.6rem", background: "rgba(201,168,76,0.15)", border: "1px solid rgba(201,168,76,0.3)", borderRadius: "2px", color: "#D4B878", cursor: "pointer", fontFamily: "'DM Sans',sans-serif", fontSize: "0.68rem" }}
                              >
                                {t("admin_confirm")}
                              </button>
                              <button
                                data-testid={`cancel-reset-${u.id}`}
                                onClick={() => { setResetId(null); setResetPwd(""); }}
                                style={{ padding: "0.3rem 0.6rem", background: "transparent", border: "1px solid rgba(201,168,76,0.15)", borderRadius: "2px", color: "#B8956A", cursor: "pointer", fontFamily: "'DM Sans',sans-serif", fontSize: "0.68rem" }}
                              >
                                {t("admin_cancel")}
                              </button>
                            </div>
                          ) : deleteId === u.id ? (
                            <div style={{ display: "flex", gap: "0.4rem" }}>
                              <button
                                data-testid={`confirm-delete-${u.id}`}
                                onClick={() => handleDelete(u.id)}
                                style={{ padding: "0.3rem 0.8rem", background: "rgba(180,60,40,0.2)", border: "1px solid rgba(180,60,40,0.4)", borderRadius: "2px", color: "#D4836A", cursor: "pointer", fontFamily: "'DM Sans',sans-serif", fontSize: "0.72rem" }}
                              >
                                {t("admin_confirm")}
                              </button>
                              <button
                                data-testid={`cancel-delete-${u.id}`}
                                onClick={() => setDeleteId(null)}
                                style={{ padding: "0.3rem 0.8rem", background: "transparent", border: "1px solid rgba(201,168,76,0.2)", borderRadius: "2px", color: "#B8956A", cursor: "pointer", fontFamily: "'DM Sans',sans-serif", fontSize: "0.72rem" }}
                              >
                                {t("admin_cancel")}
                              </button>
                            </div>
                          ) : (
                            <>
                              <button
                                data-testid={`reset-pwd-${u.id}`}
                                onClick={() => setResetId(u.id)}
                                style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(184,149,106,0.4)", padding: "0.25rem", transition: "color 0.2s" }}
                                title={t("admin_reset_password")}
                              >
                                <KeyRound size={15} />
                              </button>
                              <button
                                data-testid={`delete-user-${u.id}`}
                                onClick={() => setDeleteId(u.id)}
                                style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(184,149,106,0.4)", padding: "0.25rem", transition: "color 0.2s" }}
                              >
                                <Trash2 size={15} />
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const cellStyle = {
  fontFamily: "'DM Sans',sans-serif",
  fontSize: "0.85rem",
  color: "#EDE0C4",
  padding: "0.85rem 0.5rem",
  fontWeight: 300,
  whiteSpace: "nowrap",
};
